import { useMemo } from 'react';

import { Checkbox, CheckboxGroup } from '@/lib/base/forms/checkbox';

import type { DiscoveredObjectType } from './api';
import {
  ATTIO_OBJECT_TYPES,
  discoveredTypeToAttioObjectType,
} from './constants';

export interface AttioConfigureStepProps {
  selected: string[];
  onChange: (names: string[]) => void;
  discoveredTypes?: DiscoveredObjectType[];
  /**
   * Standard-object availability from discovery, keyed by entity type name.
   * A built-in whose entry is `false` renders disabled with "Not enabled in
   * your Attio workspace" — up front rather than hidden. Null/absent
   * (discovery failed or older backend) leaves everything enabled.
   */
  standardAvailability?: Record<string, boolean> | null;
}

const NOT_ENABLED_DESCRIPTION = 'Not enabled in your Attio workspace';

export function AttioConfigureStep({
  selected,
  onChange,
  discoveredTypes = [],
  standardAvailability = null,
}: AttioConfigureStepProps) {
  const objectTypes = useMemo(
    () => [
      ...ATTIO_OBJECT_TYPES.map(type =>
        standardAvailability?.[type.name] === false
          ? { ...type, description: NOT_ENABLED_DESCRIPTION, isDisabled: true }
          : { ...type, isDisabled: false }
      ),
      ...discoveredTypes.map(type => ({
        ...discoveredTypeToAttioObjectType(type),
        isDisabled: false,
      })),
    ],
    [discoveredTypes, standardAvailability]
  );

  return (
    <div className='flex flex-col gap-4'>
      <p className='text-sm text-[var(--color-text-secondary)]'>
        Choose which record types to bring over from Attio.
      </p>
      {/*
        The group owns the selection: react-aria routes a Checkbox nested in a
        CheckboxGroup through useCheckboxGroupItem, which reads its checked
        state from the group's value and ignores any isSelected passed to the
        child. Per-child isSelected/onChange therefore desyncs what is rendered
        from what the parent holds — keep value/onChange on the group alone.
      */}
      <CheckboxGroup
        aria-label='Record types to import'
        value={selected}
        onChange={onChange}
      >
        {objectTypes.map(type => (
          <Checkbox
            key={type.name}
            value={type.name}
            label={type.label}
            description={type.description}
            isDisabled={type.isDisabled}
          />
        ))}
      </CheckboxGroup>
    </div>
  );
}
